import { formatDate } from '@angular/common';
import { Component, Inject, LOCALE_ID, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ChartDataSets } from 'chart.js';
import * as pluginDataLabels from 'chartjs-plugin-datalabels';
import { Color, Label } from 'ng2-charts';
import { Activity } from '../models/activity';
import { ChartData } from '../models/chartData';
import { Participant } from '../models/participant';
import { Researcher } from '../models/researcher';
import { Study } from '../models/study';
import { LampService } from '../services/lamp.service';
import {StudyEventsDetailComponent} from '../study-events/study-events-detail/study-events-detail.component'
import { StudyActivitiesDetailComponent } from './study-activities-detail/study-activities-detail.component';

@Component({
  selector: 'app-study-activities',
  templateUrl: './study-activities.component.html',
  styleUrls: ['./study-activities.component.scss']
})
export class StudyActivitiesComponent implements OnInit {
  researchers: Researcher[] | undefined;
  selectedResearcher: Researcher | undefined;
  studies: Study[] | undefined;
  selectedStudy: Study | undefined;
  participants: Participant[] | undefined;
  activities: Activity[] | undefined;
  selectedActivity: Activity | undefined = new Activity;


  chartTypes: string[] = ["Bar", "Line"];
  selectedType: string = "Bar";
  chartData: ChartDataSets[] | undefined;

  startDate: Date = new Date();
  endDate: Date = new Date();

  loading: boolean = false;

  chartLabels!: Label[];
  chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      yAxes: [{
        scaleLabel: {
          display: true,
          //labelString: 'Score'
        },
        ticks: {
          min: 0,
          beginAtZero: true,
          suggestedMax: 28
        }
      }]
    },
    elements: {
      line: {
        tension: 0,
      },
    },
    plugins: {
      datalabels: {
        anchor: 'end',
        align: 'end',
        clamp: true,
        color: 'black',
        font: {
          size: 12,
          weight: 'bold'
        },
        lineTension: 0,
        display: function(context) {
          return context.dataset.data[context.dataIndex] != null; // or >= 1 or ...
       }
      }
    }
  };
  chartColors: Color[] = [];
  chartLegend = true;
  chartPlugins = [pluginDataLabels,
    {
      beforeInit: function (chart, options) {
        chart.legend.afterFit = function () {
          this.height += 20; // must use `function` and not => because of `this`
        };
      }
    }];

  constructor(private service: LampService, @Inject(LOCALE_ID) private locale: string, private snackBar: MatSnackBar, private dialog: MatDialog) {
    this.chartColors = service.getChartColors();
  }

  ngOnInit(): void {

    this.chartColors = this.service.getChartColors();

    this.service.getResearchers().subscribe(researchers => {
      this.researchers = researchers;
    });
  }

  researcherChanged() {

    this.service.getStudiesByResearcher(this.selectedResearcher?.researcherId!).subscribe(studies => {
      this.studies = studies;
    });

    this.selectedStudy = undefined;
    this.participants = undefined;
    this.chartData = undefined;
    this.activities = undefined;
  }

  studyChanged() {
    this.participants = undefined;
    this.activities = undefined;

    this.service.getParticipantsByStudy(this.selectedStudy?.studyId!).subscribe(participants => {
      this.participants = participants;
    });

    this.service.getActivitiesByStudy(this.selectedStudy.studyId, 'survey').subscribe(activities => {
      this.activities = activities;
    });

    this.chartData = undefined;
  }

  resetChart() {
    this.chartData = undefined;
  }

  verifyDates() {

    try {
      if (!this.startDate) {
        this.startDate = new Date();
      }

      if (!(this.endDate >= this.startDate)) {
        this.endDate = this.startDate
      }
    } catch (e) {

    }

    this.resetChart();
  }

  generateGraph_withoverlay() {   //   Adding in steps event to see what happens
    this.loading = true;
    this.chartData = undefined;
    this.chartLabels = new Array<string>();
    var charts = new Array<any>();


      this.service.getActivityByStudy(this.selectedStudy?.studyId!, this.selectedActivity.activityId, this.startDate, this.endDate).subscribe({
        next: events => {
          var minData = new ChartData();
          var avgData = new ChartData();
          var maxData = new ChartData();

          minData.label = this.selectedActivity.name + ' (Min)';
          avgData.label = this.selectedActivity.name + ' (Average)';
          maxData.label = this.selectedActivity.name + ' (Peak)';

          minData.data = new Array<number>();
          avgData.data = new Array<number>();
          maxData.data = new Array<number>();
          

          events.forEach(event => {
            if (event.min == null) {
              minData.data!.push(null);
            } else {
              minData.data!.push(Math.round((event.min! + Number.EPSILON) * 100) / 100);  
            }

            if (event.score == null) {
              avgData.data!.push(null);
            } else {
              avgData.data!.push(Math.round((event.score! + Number.EPSILON) * 100) / 100);  
            }

            if (event.max == null) {
              maxData.data!.push(null);
            } else {
              maxData.data!.push(Math.round((event.max! + Number.EPSILON) * 100) / 100);  
            }

            this.chartLabels.push(event.label);
          });

          (minData as any).study = this.selectedStudy;
          (minData as any).action = 'activity';
          (minData as any).actionType = this.selectedActivity.activityType;
          (minData as any).id = this.selectedActivity.activityId;

          (avgData as any).study = this.selectedStudy;
          (avgData as any).action = 'activity';
          (avgData as any).actionType = this.selectedActivity.activityType;
          (avgData as any).id = this.selectedActivity.activityId;

          (maxData as any).study = this.selectedStudy;
          (maxData as any).action = 'activity';
          (maxData as any).actionType = this.selectedActivity.activityType;
          (maxData as any).id = this.selectedActivity.activityId;

          charts.push(minData);
          charts.push(avgData);
          charts.push(maxData);

          if (charts.length > 0) {
            this.chartData = new Array<ChartDataSets>();
            charts.forEach(chart => this.chartData!.push(chart));
          }          
          this.loading = false;
        },
        error: err => {
          this.chartData = undefined;
          this.openSnackBar("No Data On This Date Range", "Close");
          this.loading = false;
        }
      });


    this.service.getStepSensorEventsByStudy(this.selectedStudy?.studyId!, "steps", this.startDate, this.endDate).subscribe({
      next: events => {

        var chartData = new Array<number>();

        events.forEach(event => {
          if (event.value == null) {
            chartData.push(null);
          } else {
            chartData.push(Math.round(((event.value! / 1000) + Number.EPSILON) * 100) / 100);
          }

          if (this.chartLabels.length != events.length) {
            this.chartLabels.push(event.label);
          }
        });

        charts.push({
          data: chartData,
          label: 'Steps (Thousands)',
          type: 'line',
          fill: false,
          study: this.selectedStudy?.studyId!,
          action: "sensor",
          actionType: "steps"
        });

          if (charts.length > 0) {
            this.chartData = new Array<ChartDataSets>();
            charts.forEach(chart => this.chartData!.push(chart));
          }
          else {
            this.openSnackBar("No Data On This Date Range", "Close");
          }

          this.loading = false;
      },
      error: err => {

          if (charts.length > 0) {
            this.chartData = new Array<ChartDataSets>();
            charts.forEach(chart => this.chartData!.push(chart));
          }
          else {
            this.openSnackBar("No Data On This Date Range", "Close");
          }

          this.loading = false;
      }
    });
  }

  generateGraph() {
    this.loading = true;
    this.chartData = undefined;
    this.chartLabels = new Array<string>();
    var charts = new Array<any>();

      this.service.getActivityByStudy(this.selectedStudy?.studyId!, this.selectedActivity.activityId, this.startDate, this.endDate).subscribe({
        next: events => {
              
          var minData = new ChartData();
          var avgData = new ChartData();
          var maxData = new ChartData();

          minData.label = this.selectedActivity.name + ' (Min)';
          avgData.label = this.selectedActivity.name + ' (Average)';
          maxData.label = this.selectedActivity.name + ' (Peak)';

          minData.data = new Array<number>();
          avgData.data = new Array<number>();
          maxData.data = new Array<number>();

          events.forEach(event => {
            if (event.min == null) {
              minData.data!.push(null);
            } else {
              minData.data!.push(Math.round((event.min! + Number.EPSILON) * 100) / 100);  
            }

            if (event.score == null) {
              avgData.data!.push(null);
            } else {
              avgData.data!.push(Math.round((event.score! + Number.EPSILON) * 100) / 100);  
            }

            if (event.max == null) {
              maxData.data!.push(null);
            } else {
              maxData.data!.push(Math.round((event.max! + Number.EPSILON) * 100) / 100);  
            }

            this.chartLabels.push(event.label);
          });

          (minData as any).study = this.selectedStudy;
          (minData as any).action = 'activity';
          (minData as any).actionType = this.selectedActivity.activityType;
          (minData as any).id = this.selectedActivity.activityId;

          (avgData as any).study = this.selectedStudy;
          (avgData as any).action = 'activity';
          (avgData as any).actionType = this.selectedActivity.activityType;
          (avgData as any).id = this.selectedActivity.activityId;

          (maxData as any).study = this.selectedStudy;
          (maxData as any).action = 'activity';
          (maxData as any).actionType = this.selectedActivity.activityType;
          (maxData as any).id = this.selectedActivity.activityId;

          charts.push(minData);
          charts.push(avgData);
          charts.push(maxData);
          this.loading = false;
          if (charts.length > 0) {
            this.chartData = new Array<ChartDataSets>();
            charts.forEach(chart => this.chartData!.push(chart));
          }
          else {
            this.openSnackBar("No Data On This Date Range", "Close");
          }
        },
        error: err => {
          this.chartData = undefined;
          this.openSnackBar("No Data On This Date Range", "Close");
          this.loading = false;
        }
      });
  }

  chartClicked(value) {

    if (value.active.length > 0) {
      var chart: any = this.chartData[value.active[0]._chart.getElementAtEvent(value.event)[0]._datasetIndex];
      if (chart.action == 'sensor') {
        let dialogRef = this.dialog.open(StudyEventsDetailComponent, {
          data: {
            study: this.selectedStudy,
            event: chart.actionType,
            start: this.startDate,
            end: this.endDate,
            index: value.active[0]._index
          }, disableClose: true, width: '105%', height: '95%', maxHeight: '95%'
        });

        dialogRef.afterClosed().subscribe();
      }
      else {
        let dialogRef = this.dialog.open(StudyActivitiesDetailComponent, {
          data: {
            study: this.selectedStudy,
            activity: chart.id,
            start: this.startDate,
            end: this.endDate,
            index: value.active[0]._index
          }, disableClose: true, width: '105%', height: '95%', maxHeight: '95%'
        });

        dialogRef.afterClosed().subscribe();
      }

      

      //  FIX HERE MAYBE:  https://medium.com/beereal/making-ng2-charts-work-for-us-553f4229665

    }
  }

  openSnackBar(message: string, action: string) {
    this.snackBar.open(message, action, {
      duration: 2000,
    });
  }
}
